/**
 * TabProduccion — Fase 3: Grabación/Producción.
 * 
 * Tablero Kanban interactivo con:
 * - Creación, edición y eliminación de items de producción.
 * - Columnas: Por Hacer, En Proceso, Revisión, Listo.
 * - Categorías: Grabación (Video), Edición (Tijeras), Diseños Canva (Paleta).
 * - Filtro por categoría y movimiento ágil de tarjetas.
 */

import { useEffect, useState } from 'react';
import {
  Video,
  Scissors,
  Palette,
  Plus,
  Loader2,
  Trash2,
  X,
  Edit2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Filter,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { ProductionItem } from '@/types';

interface Props {
  clientId: string;
  period: string;
}

const COLUMNS = [
  { id: 'todo', label: 'Por Hacer', color: 'bg-slate-100/90 dark:bg-slate-800/80', badge: 'bg-slate-200 text-slate-700' },
  { id: 'in_progress', label: 'En Proceso', color: 'bg-blue-50/80 dark:bg-blue-950/60', badge: 'bg-blue-200 text-blue-800' },
  { id: 'review', label: 'Revisión', color: 'bg-amber-50/80 dark:bg-amber-950/60', badge: 'bg-amber-200 text-amber-800' },
  { id: 'done', label: 'Listo ✓', color: 'bg-emerald-50/80 dark:bg-emerald-950/60', badge: 'bg-emerald-200 text-emerald-800' },
];

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  recording: <Video size={14} className="text-red-500" />,
  editing: <Scissors size={14} className="text-blue-500" />,
  design: <Palette size={14} className="text-pink-500" />,
};

const CATEGORY_LABELS: Record<string, string> = {
  recording: 'Grabación',
  editing: 'Edición',
  design: 'Diseño',
};

export function TabProduccion({ clientId, period }: Props) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Modal Crear / Editar
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductionItem | null>(null);
  const [itemForm, setItemForm] = useState({
    title: '',
    category: 'recording' as ProductionItem['category'],
    description: '',
    status: 'todo' as ProductionItem['status'],
  });
  const [savingItem, setSavingItem] = useState(false);

  const loadItems = () => {
    api
      .getProductionItems(clientId, period)
      .then((res) => setItems(res.production as ProductionItem[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    loadItems();
  }, [clientId, period]);

  // ─── Handlers Modal ───
  const openNewItemModal = (defaultStatus = 'todo') => {
    setEditingItem(null);
    setItemForm({
      title: '',
      category: 'recording',
      description: '',
      status: defaultStatus as ProductionItem['status'],
    });
    setModalOpen(true);
  };

  const openEditItemModal = (item: ProductionItem) => {
    setEditingItem(item);
    setItemForm({
      title: item.title,
      category: item.category || 'recording',
      description: item.description || '',
      status: item.status || 'todo',
    });
    setModalOpen(true);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemForm.title) return;

    setSavingItem(true);
    try {
      if (editingItem) {
        await api.updateProductionItem(editingItem.id, {
          ...itemForm,
          client_id: clientId,
          period,
        });
      } else {
        await api.createProductionItem({
          ...itemForm,
          client_id: clientId,
          period,
        });
      }
      setModalOpen(false);
      loadItems();
    } catch (error) {
      console.error('Error guardando item de producción:', error);
      alert('Error guardando item en el tablero');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm('¿Eliminar este item de producción?')) return;
    try {
      await api.deleteProductionItem(id);
      setModalOpen(false);
      loadItems();
    } catch (error) {
      console.error('Error eliminando item:', error);
    }
  };

  // Mover tarjeta entre estados
  const handleMoveItem = async (itemId: string, newStatus: string) => {
    try {
      await api.updateProductionItemStatus(itemId, newStatus);
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, status: newStatus as ProductionItem['status'] } : item
        )
      );
    } catch (error) {
      console.error('Error moviendo item:', error);
    }
  };

  // Filtrar items
  const filteredItems = items.filter((item) => {
    if (categoryFilter === 'all') return true;
    return item.category === categoryFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ─── Cabecera y Filtros ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Tablero de Producción</h3>
          <p className="text-xs text-slate-500">
            Gestiona la grabación, edición y diseño de piezas para el período {period}.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Filtro por Categoría */}
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5 shadow-2xs text-xs">
            <span className="px-2 text-slate-400 flex items-center gap-1">
              <Filter size={12} />
              Filtrar:
            </span>
            <button
              onClick={() => setCategoryFilter('all')}
              className={cn(
                'px-2 py-1 rounded-md transition-colors',
                categoryFilter === 'all'
                  ? 'bg-slate-900 text-white font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              Todos
            </button>
            <button
              onClick={() => setCategoryFilter('recording')}
              className={cn(
                'px-2 py-1 rounded-md transition-colors flex items-center gap-1',
                categoryFilter === 'recording'
                  ? 'bg-red-600 text-white font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <Video size={11} /> Grabación
            </button>
            <button
              onClick={() => setCategoryFilter('editing')}
              className={cn(
                'px-2 py-1 rounded-md transition-colors flex items-center gap-1',
                categoryFilter === 'editing'
                  ? 'bg-blue-600 text-white font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <Scissors size={11} /> Edición
            </button>
            <button
              onClick={() => setCategoryFilter('design')}
              className={cn(
                'px-2 py-1 rounded-md transition-colors flex items-center gap-1',
                categoryFilter === 'design'
                  ? 'bg-pink-600 text-white font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <Palette size={11} /> Diseño
            </button>
          </div>

          <Button size="sm" onClick={() => openNewItemModal()} className="gap-1 shadow-sm text-xs">
            <Plus size={14} />
            Nuevo Item
          </Button>
        </div>
      </div>

      {/* ─── Tablero Kanban ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((column, colIndex) => {
          const columnItems = filteredItems.filter((i) => i.status === column.id);
          const prevCol = colIndex > 0 ? COLUMNS[colIndex - 1] : null;
          const nextCol = colIndex < COLUMNS.length - 1 ? COLUMNS[colIndex + 1] : null;

          return (
            <div
              key={column.id}
              className={cn(
                'rounded-2xl p-3.5 min-h-[400px] border border-slate-200/80 shadow-2xs flex flex-col',
                column.color
              )}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-bold text-slate-800">{column.label}</h4>
                  <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', column.badge)}>
                    {columnItems.length}
                  </span>
                </div>
                <button
                  onClick={() => openNewItemModal(column.id)}
                  className="p-1 hover:bg-black/5 rounded text-slate-500 hover:text-slate-800 transition-colors"
                  title={`Agregar item a ${column.label}`}
                >
                  <Plus size={15} />
                </button>
              </div>

              <div className="space-y-2.5 flex-1">
                {columnItems.map((item) => (
                  <Card
                    key={item.id}
                    className="border border-slate-200/90 shadow-2xs hover:shadow-sm transition-all bg-white group cursor-pointer"
                    onClick={() => openEditItemModal(item)}
                  >
                    <CardContent className="p-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="p-1 bg-slate-50 rounded border border-slate-100">
                            {CATEGORY_ICONS[item.category]}
                          </span>
                          <span className="text-[11px] font-medium text-slate-500">
                            {CATEGORY_LABELS[item.category]}
                          </span>
                        </div>

                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditItemModal(item);
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 rounded"
                            title="Editar"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600 rounded"
                            title="Eliminar"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {item.title}
                      </p>

                      {item.description && (
                        <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      )}

                      {/* Botones de avance rápido en el flujo */}
                      <div
                        className="flex items-center justify-between gap-1 mt-3 pt-2 border-t border-slate-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {prevCol ? (
                          <button
                            onClick={() => handleMoveItem(item.id, prevCol.id)}
                            className="text-[10px] font-medium px-2 py-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 flex items-center gap-1 transition-colors border border-slate-200/60"
                            title={`Mover atrás a ${prevCol.label}`}
                          >
                            <ArrowLeft size={10} />
                            {prevCol.label.split(' ')[0]}
                          </button>
                        ) : (
                          <div />
                        )}

                        {nextCol && (
                          <button
                            onClick={() => handleMoveItem(item.id, nextCol.id)}
                            className="text-[10px] font-medium px-2 py-1 rounded bg-slate-50 hover:bg-blue-50 hover:text-blue-600 text-slate-600 flex items-center gap-1 transition-colors border border-slate-200/60 ml-auto"
                            title={`Avanzar a ${nextCol.label}`}
                          >
                            {nextCol.label.split(' ')[0]}
                            <ArrowRight size={10} />
                          </button>
                        )}

                        {column.id === 'done' && (
                          <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1 ml-auto">
                            <CheckCircle2 size={12} /> Listo
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {columnItems.length === 0 && (
                  <div className="h-32 border-2 border-dashed border-slate-200/60 rounded-xl flex flex-col items-center justify-center text-slate-400 gap-1 p-3">
                    <p className="text-xs italic">Sin items</p>
                    <button
                      onClick={() => openNewItemModal(column.id)}
                      className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5"
                    >
                      <Plus size={11} /> Agregar
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Modal: Crear / Editar Item de Producción ─── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingItem ? 'Editar Item de Producción' : 'Nuevo Item de Producción'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Título del Item *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Grabar tomas B-roll en estudio de luz natural"
                  value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Categoría
                  </label>
                  <select
                    value={itemForm.category}
                    onChange={(e) =>
                      setItemForm({
                        ...itemForm,
                        category: e.target.value as ProductionItem['category'],
                      })
                    }
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  >
                    <option value="recording">🎥 Grabación (Video/Fotos)</option>
                    <option value="editing">✂️ Edición (Reels/CapCut)</option>
                    <option value="design">🎨 Diseño (Canva/Flyers)</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Columna / Estado
                  </label>
                  <select
                    value={itemForm.status}
                    onChange={(e) =>
                      setItemForm({
                        ...itemForm,
                        status: e.target.value as ProductionItem['status'],
                      })
                    }
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  >
                    <option value="todo">Por Hacer</option>
                    <option value="in_progress">En Proceso</option>
                    <option value="review">En Revisión</option>
                    <option value="done">Listo ✓</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Instrucciones o Descripción
                </label>
                <textarea
                  rows={3}
                  placeholder="Pautas de grabación, resolución, música de referencia o detalles..."
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                {editingItem ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteItem(editingItem.id)}
                    className="text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1"
                  >
                    <Trash2 size={13} />
                    Eliminar
                  </Button>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setModalOpen(false)}
                    className="text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={savingItem}
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {savingItem ? <Loader2 size={13} className="animate-spin" /> : 'Guardar Item'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
